import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  ConfigUpdate,
  EvalConfig,
  EvalSample,
} from "@tsmono/inspect-common/types";
import { effectiveEvalConfig } from "@tsmono/inspect-common/utils";
import { useUnmount } from "@tsmono/react/hooks";
import { AsyncData, createLogger, isRecord } from "@tsmono/util";

import { getApi, useLogDir } from "../app_config";
import {
  useCurrentLogFile,
  useCurrentSampleHandle,
} from "../app/routing/currentSelection";
import {
  createEvalDescriptor,
  createSamplesDescriptor,
} from "../app/samples/descriptor/samplesDescriptor";
import { ScoreView } from "../app/samples/header-v2/ViewToggle";
import { filterSamples } from "../app/samples/sample-tools/filters";
import { sampleIdsEqual } from "../app/shared/sample";
import { LogHeader, RunningMetric, SampleSummary } from "../client/api/types";
import {
  useEvalSampleData,
  useLogHeader,
  usePassiveEvalSampleData,
  useRunningMetrics,
  useSampleSummaries,
  type EvalSampleData,
} from "../log_data";

import { refreshLog } from "./actions";
import { getAvailableScorers } from "./scoring";
import { useStore } from "./store";

const kScorePanelViewBag = "score-panel-view";
const kScorePanelViewKey = "view";
const kScorePanelSortBag = "score-panel-sort";
const kScorePanelSortKey = "sort";

export type ScorePanelSortColumn = "name" | "value" | null;
export interface ScorePanelSortState {
  column: ScorePanelSortColumn;
  dir: "asc" | "desc";
}
// Property bags hold unknown — these are what the casts stood in for.
const isScoreView = (value: unknown): value is ScoreView =>
  value === "grid" || value === "chips";

const isScorePanelSortState = (value: unknown): value is ScorePanelSortState =>
  isRecord(value) &&
  (value["dir"] === "asc" || value["dir"] === "desc") &&
  (value["column"] === null || typeof value["column"] === "string");

const kDefaultScorePanelSort: ScorePanelSortState = {
  column: null,
  dir: "asc",
};

/**
 * Read / write the user's preferred V2 score panel view (chips vs grid).
 * Persisted globally via the app property bag so the choice carries
 * across samples. Returns the *stored* value; callers resolve their
 * own default (typically `chips` for ≤ 6 scores, `grid` for 7+).
 */
export const useScorePanelView = (): [
  ScoreView | undefined,
  (view: ScoreView) => void,
] => {
  const stored = useStore((state) => {
    const value =
      state.app.propertyBags[kScorePanelViewBag]?.[kScorePanelViewKey];
    return isScoreView(value) ? value : undefined;
  });
  const setPropertyValue = useStore(
    (state) => state.appActions.setPropertyValue
  );
  const setView = useCallback(
    (view: ScoreView) => {
      setPropertyValue(kScorePanelViewBag, kScorePanelViewKey, view);
    },
    [setPropertyValue]
  );
  return [stored, setView];
};

/**
 * Resolve the stored / eval-supplied / default view given the score count.
 * Priority: user override (stored) > eval default > built-in count rule.
 */
export const resolveScorePanelView = (
  stored: ScoreView | undefined,
  evalDefault: ScoreView | undefined,
  count: number
): ScoreView => stored ?? evalDefault ?? (count <= 6 ? "chips" : "grid");

/**
 * Read / write the user's V2 score panel sort. Persisted globally via
 * the app property bag (mirrors `useScorePanelView`) so the sort
 * carries across samples in the same session. Returns the *stored*
 * value; callers resolve their own default via `resolveScorePanelSort`.
 */
export const useScorePanelSort = (): [
  ScorePanelSortState | undefined,
  (sort: ScorePanelSortState) => void,
] => {
  const stored = useStore((state) => {
    const value =
      state.app.propertyBags[kScorePanelSortBag]?.[kScorePanelSortKey];
    return isScorePanelSortState(value) ? value : undefined;
  });
  const setPropertyValue = useStore(
    (state) => state.appActions.setPropertyValue
  );
  const setSort = useCallback(
    (sort: ScorePanelSortState) => {
      setPropertyValue(kScorePanelSortBag, kScorePanelSortKey, sort);
    },
    [setPropertyValue]
  );
  return [stored, setSort];
};

/**
 * Resolve the stored / eval-supplied / default sort.
 * Priority: user override (stored) > eval default > unsorted.
 */
export const resolveScorePanelSort = (
  stored: ScorePanelSortState | undefined,
  evalDefault: ScorePanelSortState | undefined
): ScorePanelSortState => stored ?? evalDefault ?? kDefaultScorePanelSort;

// The eval-author default mode was serialized under `view` and is now
// serialized under `default`; accept either so logs from either version
// resolve. The value is advisory, so a miss falls through to the
// count-based default downstream.
export const readEvalScorePanelView = (
  panel:
    { default?: ScoreView | null; view?: ScoreView | null } | null | undefined
): ScoreView | undefined => panel?.default ?? panel?.view ?? undefined;

/**
 * Read the eval-author-declared default score-panel view from
 * `Task(viewer=ViewerConfig(sample_score_view=SampleScoreView(default=...)))`.
 * Returns a primitive so Zustand's reference equality is stable.
 */
export const useEvalScorePanelView = (): ScoreView | undefined =>
  readEvalScorePanelView(
    useSelectedLogDetails()?.eval.viewer?.sample_score_view
  );

/**
 * Read the eval-author-declared default score-panel sort. The raw
 * stored object reference is stable across renders (it lives on
 * `selectedLogDetails`); we normalize the nullable fields inside a
 * `useMemo` keyed on that reference to avoid feeding a fresh object
 * back into Zustand on every render.
 */
export const useEvalScorePanelSort = (): ScorePanelSortState | undefined => {
  const stored = useSelectedLogDetails()?.eval.viewer?.sample_score_view?.sort;
  return useMemo(() => {
    if (!stored) return undefined;
    return {
      column: stored.column ?? null,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      dir: stored.dir ?? "asc",
    };
  }, [stored]);
};

const log = createLogger("hooks");

/**
 * The details for the currently selected log. Returns the settled value (or
 * `undefined` while loading / when no log is selected) — the drop-in for the
 * retired `log.selectedLogDetails`. Use `useLogHeader` directly for the
 * loading/error surface.
 */
export const useSelectedLogDetails = (): LogHeader | undefined => {
  const logDir = useLogDir();
  const selectedLogFile = useCurrentLogFile();
  return useLogHeader(logDir, selectedLogFile, { demand: "passive" }).data;
};

export const useEvalSpec = () => {
  return useSelectedLogDetails()?.eval;
};

/** The selected log's mid-run config changes (undefined when none). */
export const useConfigUpdates = (): ConfigUpdate[] | undefined => {
  return useSelectedLogDetails()?.config_updates ?? undefined;
};

/**
 * The eval config the selected run actually ran under — launch config with
 * `config_updates` folded in. Functional readers must use this rather than
 * `evalSpec.config` so mid-run retunes are honored.
 */
export const useEffectiveEvalConfig = (): EvalConfig | undefined => {
  const details = useSelectedLogDetails();
  const config = details?.eval.config;
  const configUpdates = details?.config_updates;
  return useMemo(() => {
    if (!config) return undefined;
    return effectiveEvalConfig(config, configUpdates);
  }, [config, configUpdates]);
};

/**
 * The selected log's running metrics — the selection binding over the
 * param-driven `useRunningMetrics` acquisition hook.
 */
export const useSelectedRunningMetrics = (): AsyncData<
  RunningMetric[] | undefined
> => {
  const logDir = useLogDir();
  const selectedLogFile = useCurrentLogFile();
  return useRunningMetrics(logDir, selectedLogFile);
};

export interface LogEditAffordance {
  /** True when an edit can be initiated: server supports edits, a log is
   *  selected, and the recorder isn't still appending. */
  canEdit: boolean;
  /** The log file the edit would target, or undefined. */
  selectedLogFile: string | undefined;
  /** Call after a successful edit to re-read the log into the store. */
  refreshOnSave: () => void;
}

/**
 * Capability + plumbing for an edit-the-current-log surface (tag, metadata,
 * score, …). Each edit dialog kind reads this and renders its own dialog
 * with its own payload — but the gate and the refresh wiring are shared.
 *
 * The in-progress gate mirrors the server: the edit API returns 409 while
 * the recorder still owns the file, so offering the action and failing
 * on save is worse than not offering it.
 */
export const useLogEditAffordance = (): LogEditAffordance => {
  const api = getApi();
  const hasEditApi = Boolean(api.edit_log);
  const selectedLogFile = useCurrentLogFile();
  const logStatus = useSelectedLogDetails()?.status;
  const isInProgress = logStatus === "started";
  return {
    canEdit: hasEditApi && !!selectedLogFile && !isInProgress,
    selectedLogFile,
    refreshOnSave: () => refreshLog(selectedLogFile),
  };
};

/**
 * The selected log's sample summaries (completed and incomplete, unfiltered)
 * — the selection binding over the param-driven `useSampleSummaries`
 * acquisition hook.
 */
export const useSelectedSampleSummaries = (): AsyncData<SampleSummary[]> => {
  const logDir = useLogDir();
  const selectedLogFile = useCurrentLogFile();
  return useSampleSummaries(logDir, selectedLogFile);
};

const kNoSummaries: SampleSummary[] = [];

// The settled rows for the derivation hooks below — pure computations over
// whatever has settled (loading/error render in SamplesTab, which reads the
// AsyncData binding directly).
const useSelectedSampleSummariesData = (): SampleSummary[] =>
  useSelectedSampleSummaries().data ?? kNoSummaries;

// Counts the total number of unfiltered sample summaries (both complete and incomplete)
export const useTotalSampleCount = () => {
  const sampleSummaries = useSelectedSampleSummariesData();
  return useMemo(() => {
    return sampleSummaries.length;
  }, [sampleSummaries]);
};

// Provides the currently selected score(s) for this eval, providing a default
// based upon the configuration (eval + summaries) if no scorer has been
// selected
export const useSelectedScores = () => {
  const selectedLogDetails = useSelectedLogDetails();
  const sampleSummaries = useSelectedSampleSummariesData();
  const selected = useStore((state) => state.log.selectedScores);
  return useMemo(() => {
    if (selected !== undefined) {
      return selected;
    }
    if (selectedLogDetails) {
      return getAvailableScorers(selectedLogDetails, sampleSummaries) ?? [];
    }
    return [];
  }, [selectedLogDetails, sampleSummaries, selected]);
};

// Provides the list of available scorers. Will inspect the eval or samples
// to determine scores (even for in progress evals that don't yet have final
// metrics)
export const useScores = () => {
  const selectedLogDetails = useSelectedLogDetails();
  const sampleSummaries = useSelectedSampleSummariesData();
  return useMemo(() => {
    if (!selectedLogDetails) {
      return [];
    }

    const result =
      getAvailableScorers(selectedLogDetails, sampleSummaries) || [];
    return result;
  }, [selectedLogDetails, sampleSummaries]);
};

// Provides the eval descriptor
export const useEvalDescriptor = () => {
  const scores = useScores();
  const sampleSummaries = useSelectedSampleSummariesData();
  return useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return scores ? createEvalDescriptor(scores, sampleSummaries) : null;
  }, [scores, sampleSummaries]);
};

export const useSampleDescriptor = () => {
  const evalDescriptor = useEvalDescriptor();
  const sampleSummaries = useSelectedSampleSummariesData();
  const selectedScores = useSelectedScores();
  return useMemo(() => {
    return evalDescriptor
      ? createSamplesDescriptor(sampleSummaries, evalDescriptor, selectedScores)
      : undefined;
  }, [evalDescriptor, sampleSummaries, selectedScores]);
};

// Sort key: sample id ascending (numeric when both numeric, else
// lexicographic), then epoch ascending. Extracted so the already-sorted
// pre-check shares the exact comparison the sort uses.
export const compareSamples = (a: SampleSummary, b: SampleSummary): number => {
  let idCompare: number;
  if (typeof a.id === "number" && typeof b.id === "number") {
    idCompare = a.id - b.id;
  } else {
    idCompare = String(a.id).localeCompare(String(b.id));
  }
  if (idCompare !== 0) {
    return idCompare;
  }
  return a.epoch - b.epoch;
};

// Server summaries usually arrive already sorted; this lets useFilteredSamples
// skip an O(n log n) clone + sort on every filter/store change.
export const samplesAreSorted = (samples: SampleSummary[]): boolean =>
  samples.every((curr, i) => {
    const prev = samples[i - 1];
    return prev === undefined || compareSamples(prev, curr) <= 0;
  });

// Provides the list of filtered and sorted samples
export const useFilteredSamples = () => {
  const samplesDescriptor = useSampleDescriptor();
  const sampleSummaries = useSelectedSampleSummariesData();
  const filter = useStore((state) => state.log.filter);
  const setFilterError = useStore((state) => state.logActions.setFilterError);
  const clearFilterError = useStore(
    (state) => state.logActions.clearFilterError
  );

  const { samples, filterError } = useMemo(() => {
    // Apply text filter
    const { result, error, allErrors } =
      samplesDescriptor && filter
        ? filterSamples(samplesDescriptor, sampleSummaries, filter)
        : { result: sampleSummaries, error: undefined, allErrors: false };

    // A filter that errored on every sample is reported rather than applied —
    // the unfiltered list stays visible under the error annotation.
    const failedAllSamples = error !== undefined && allErrors;

    const filtered = failedAllSamples ? sampleSummaries : result;

    // Skip the clone + sort when the list is already ordered (the common case).
    const sorted =
      filtered.length < 2 || samplesAreSorted(filtered)
        ? filtered
        : [...filtered].sort(compareSamples);

    return {
      samples: sorted,
      filterError: failedAllSamples ? error : undefined,
    };
  }, [samplesDescriptor, sampleSummaries, filter]);

  // Publishing the error is a side effect, so it belongs in an effect rather
  // than the memo above: a store write during render makes SampleFilter update
  // while SamplesTab is rendering, and leaves the memo impure — which React
  // Compiler is free to cache, stranding a stale error on the filter input.
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (filterError) {
      setFilterError(filterError);
    } else {
      clearFilterError();
    }
  }, [filterError, setFilterError, clearFilterError]);

  return samples;
};

// Provides the currently selected sample summary
export const useSelectedSampleSummary = (): SampleSummary | undefined => {
  const sampleSummaries = useSelectedSampleSummariesData();
  const sampleHandle = useCurrentSampleHandle();
  return useMemo(() => {
    const selectedSampleSummary = sampleSummaries.find((sample) => {
      return (
        sampleIdsEqual(sample.id, sampleHandle?.id) &&
        sample.epoch === sampleHandle?.epoch
      );
    });

    return selectedSampleSummary;
  }, [sampleHandle, sampleSummaries]);
};

/**
 * The selected sample's data — the selection binding over the param-driven
 * `useEvalSampleData` acquisition hook.
 */
export const useSelectedEvalSampleData = (): EvalSampleData => {
  const logDir = useLogDir();
  const handle = useCurrentSampleHandle();
  return useEvalSampleData(logDir, handle);
};

/**
 * The selected sample's invalidation record (if any) — the selection binding
 * projecting from the param-driven `usePassiveEvalSampleData` acquisition
 * hook.
 */
export const useSelectedSampleInvalidation = ():
  EvalSample["invalidation"] | undefined =>
  usePassiveEvalSampleData(useCurrentSampleHandle()).data?.sample
    ?.invalidation ?? undefined;

export const useCollapseSampleEvent = (
  scope: string,
  id: string
): [boolean, (collapsed: boolean) => void] => {
  const collapsed = useStore((state) => state.sample.collapsedEvents);
  const collapseEvent = useStore((state) => state.sampleActions.collapseEvent);

  return useMemo(() => {
    const isCollapsed = collapsed !== null && collapsed[scope]?.[id] === true;
    const set = (value: boolean) => {
      log.debug("Set collapsed", id, value);
      collapseEvent(scope, id, value);
    };
    return [isCollapsed, set];
  }, [collapsed, scope, id, collapseEvent]);
};

export const useMessageVisibility = (
  id: string,
  scope: "sample" | "eval"
): [boolean, (visible: boolean) => void] => {
  const visible = useStore((state) =>
    state.appActions.getMessageVisible(id, true)
  );
  const setVisible = useStore((state) => state.appActions.setMessageVisible);
  const clearVisible = useStore(
    (state) => state.appActions.clearMessageVisible
  );

  // Track if this is the first render (rehydrate)
  const isFirstRender = useRef(true);

  // Reset state if the eval changes, but not during initialization
  const selectedLogFile = useCurrentLogFile();
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    // Skip the first effect run
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    log.debug("clear message (eval)", id);
    clearVisible(id);
  }, [selectedLogFile, clearVisible, id]);

  // Maybe reset state if sample changes
  const sampleHandle = useCurrentSampleHandle();

  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    // Skip the first effect run for sample changes too
    if (isFirstRender.current) {
      return;
    }

    if (scope === "sample") {
      log.debug("clear message (sample)", id);
      clearVisible(id);
    }
  }, [
    sampleHandle?.logFile,
    sampleHandle?.id,
    sampleHandle?.epoch,
    clearVisible,
    id,
    scope,
  ]);

  return useMemo(() => {
    log.debug("visibility", id, visible);
    const set = (visible: boolean) => {
      log.debug("set visiblity", id);
      setVisible(id, visible);
    };
    return [visible, set];
  }, [visible, setVisible, id]);
};

export const useSamplePopover = (id: string) => {
  const setVisiblePopover = useStore(
    (store) => store.sampleActions.setVisiblePopover
  );
  const clearVisiblePopover = useStore(
    (store) => store.sampleActions.clearVisiblePopover
  );
  const visiblePopover = useStore((store) => store.sample.visiblePopover);
  const timerRef = useRef<number | null>(null);

  const show = useCallback(() => {
    if (timerRef.current) {
      return; // Timer already running
    }

    timerRef.current = window.setTimeout(() => {
      setVisiblePopover(id);
      timerRef.current = null;
    }, 250);
  }, [id, setVisiblePopover]);

  const hide = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    clearVisiblePopover();
  }, [clearVisiblePopover]);

  // Clear the timeout when component unmounts
  useUnmount(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  });

  const isShowing = useMemo(() => {
    return visiblePopover === id;
  }, [id, visiblePopover]);

  return {
    show,
    hide,
    setShowing: (shouldShow: boolean) => {
      if (shouldShow) {
        show();
      } else {
        hide();
      }
    },
    isShowing,
  };
};

export const useLogsListing = () => {
  const gridStateByScope = useStore(
    (state) => state.logs.listing.gridStateByScope
  );
  const patchGridState = useStore(
    (state) => state.logsActions.patchLogsGridState
  );
  const clearGridState = useStore(
    (state) => state.logsActions.clearLogsGridState
  );

  return {
    gridStateByScope,
    patchGridState,
    clearGridState,
  };
};
